import { useCallback, useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';

import PulseShieldLoader from './PulseShieldLoader';

type ReplayVideoCardProps = {
  src: string;
  alt: string;
  isDarkTheme?: boolean;
};

const READY_BUFFER_THRESHOLD = 18;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  cancelIdleCallback?: (handle: number) => void;
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function getBufferedProgress(video: HTMLVideoElement) {
  const duration = video.duration;

  if (!Number.isFinite(duration) || duration <= 0) {
    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) return 100;
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return 80;
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return 40;
    return 0;
  }

  const buffered = video.buffered;
  if (!buffered || buffered.length === 0) {
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return 100;
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return 30;
    return 0;
  }

  const bufferedEnd = buffered.end(buffered.length - 1);
  return clamp((bufferedEnd / duration) * 100, 0, 100);
}

function isReadyEnough(video: HTMLVideoElement) {
  return getBufferedProgress(video) >= READY_BUFFER_THRESHOLD || video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
}

export default function ReplayVideoCard({ src, alt, isDarkTheme = false }: ReplayVideoCardProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const preloadStartedRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBufferingForPlay, setIsBufferingForPlay] = useState(false);

  const syncVideoState = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const nextProgress = Math.round(getBufferedProgress(video));
    setProgress((current) => (current === nextProgress ? current : nextProgress));
  }, []);

  const startBackgroundPreload = useCallback(() => {
    const video = videoRef.current;
    if (!video || preloadStartedRef.current) return;

    preloadStartedRef.current = true;
    video.preload = 'auto';
    video.load();
    syncVideoState();
  }, [syncVideoState]);

  const waitForReady = useCallback(() => {
    const currentVideo = videoRef.current;
    if (!currentVideo) return Promise.resolve();
    const video: HTMLVideoElement = currentVideo;

    if (isReadyEnough(video)) {
      syncVideoState();
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let settled = false;
      const intervalId = window.setInterval(checkReady, 180);

      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.clearInterval(intervalId);
        video.removeEventListener('progress', checkReady);
        video.removeEventListener('canplay', checkReady);
        video.removeEventListener('loadeddata', checkReady);
        video.removeEventListener('loadedmetadata', checkReady);
        video.removeEventListener('durationchange', checkReady);
        video.removeEventListener('stalled', checkReady);
        video.removeEventListener('waiting', checkReady);
        video.removeEventListener('canplaythrough', handleCanPlayThrough);
      };

      const handleCanPlayThrough = () => {
        setProgress(100);
        cleanup();
        resolve();
      };

      function checkReady() {
        syncVideoState();
        if (isReadyEnough(video)) {
          cleanup();
          resolve();
        }
      }

      video.addEventListener('progress', checkReady);
      video.addEventListener('canplay', checkReady);
      video.addEventListener('loadeddata', checkReady);
      video.addEventListener('loadedmetadata', checkReady);
      video.addEventListener('durationchange', checkReady);
      video.addEventListener('stalled', checkReady);
      video.addEventListener('waiting', checkReady);
      video.addEventListener('canplaythrough', handleCanPlayThrough);
      checkReady();
    });
  }, [syncVideoState]);

  useEffect(() => {
    let cancelled = false;
    let cancelIdle: (() => void) | undefined;

    const schedulePreload = () => {
      if (cancelled) return;
      const idleWindow = window as IdleWindow;

      if (typeof idleWindow.requestIdleCallback === 'function') {
        const idleId = idleWindow.requestIdleCallback(() => {
          if (!cancelled) startBackgroundPreload();
        }, { timeout: 2000 });
        cancelIdle = () => idleWindow.cancelIdleCallback?.(idleId);
        return;
      }

      const timeoutId = window.setTimeout(() => {
        if (!cancelled) startBackgroundPreload();
      }, 0);
      cancelIdle = () => window.clearTimeout(timeoutId);
    };

    if (document.readyState === 'complete') {
      schedulePreload();
    } else {
      window.addEventListener('load', schedulePreload, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', schedulePreload);
      cancelIdle?.();
    };
  }, [startBackgroundPreload]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoadedMetadata = () => syncVideoState();
    const onProgress = () => syncVideoState();
    const onCanPlay = () => syncVideoState();
    const onCanPlayThrough = () => {
      setProgress(100);
      syncVideoState();
    };
    const onPlaying = () => {
      setIsPlaying(true);
      setIsBufferingForPlay(false);
    };
    const onEnded = () => {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
      setIsBufferingForPlay(false);
      syncVideoState();
    };
    const onPause = () => {
      if (!video.ended) setIsPlaying(false);
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('progress', onProgress);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('canplaythrough', onCanPlayThrough);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('ended', onEnded);
    video.addEventListener('pause', onPause);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('canplaythrough', onCanPlayThrough);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('pause', onPause);
    };
  }, [syncVideoState]);

  const handlePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    startBackgroundPreload();

    if (!isReadyEnough(video)) {
      setIsBufferingForPlay(true);
      syncVideoState();
      await waitForReady();
    }

    setIsBufferingForPlay(false);

    try {
      await video.play();
    } catch {
      setIsPlaying(false);
      setIsBufferingForPlay(false);
    }
  };

  const showOverlay = !isPlaying && !isBufferingForPlay;

  return (
    <div className={`flex h-full flex-col rounded-[1.5rem] border p-1.5 shadow-[0_20px_50px_rgba(15,23,42,0.06)] sm:rounded-[2rem] sm:p-4 ${isDarkTheme ? 'border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]' : 'border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)]'}`}>
      <div className="relative flex-1 overflow-hidden rounded-[1.1rem] border border-slate-200 bg-white sm:rounded-[1.5rem]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(96,165,250,0.06),_transparent_45%),radial-gradient(circle_at_80%_18%,_rgba(168,85,247,0.05),_transparent_34%)]" />

        <video
          ref={videoRef}
          className="relative z-10 block h-full w-full object-contain"
          muted
          playsInline
          preload="metadata"
          aria-label={alt}
          controls={false}
        >
          <source src={src} type="video/webm" />
        </video>

        <div className="absolute inset-0 z-10 bg-gradient-to-tr from-white/0 via-white/10 to-white/20 opacity-35" />

        <button
          type="button"
          onClick={handlePlay}
          aria-label="Play replay video"
          className={`absolute inset-0 z-30 flex items-center justify-center transition-opacity duration-300 ${showOverlay ? 'cursor-pointer opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/20 bg-white/95 text-slate-950 shadow-[0_18px_48px_rgba(15,23,42,0.28),0_0_0_10px_rgba(255,255,255,0.08)] transition-transform duration-300 hover:scale-105 active:scale-95">
            <Play className="ml-1 h-7 w-7 fill-current" />
          </span>
        </button>

        <PulseShieldLoader
          progress={progress}
          isVisible={isBufferingForPlay}
          label="Loading Replay"
          sublabel="Fetching the replay asset inside the card"
        />
      </div>
    </div>
  );
}
